<template>
  <div class="salon-app salon-location">
    <!-- Top app bar + booking progress -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <!-- SH020 — not a Back arrow at all on step 1: there is nothing behind the first
             step of the flow. It opens the customer's booking history, so it is only drawn
             when they are signed in and actually have one. -->
        <button
          v-if="hasBookings"
          class="salon-back"
          type="button"
          :aria-label="$t('salon.location.myBookings')"
          @click="$emit('bookings')"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.flow.salonBooking") }}</h1>
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.flow.stepOf", { step, total: totalSteps }) }}</span>
      </div>
    </header>

    <main class="salon-main">
      <section class="salon-intro">
        <h2 class="salon-intro-title">{{ $t("salon.location.title") }}</h2>
        <p class="salon-intro-subtitle">{{ $t("salon.location.subtitle") }}</p>
      </section>

      <div class="salon-search">
        <svg class="salon-search-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2" />
          <path d="M20 20l-3.5-3.5" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" />
        </svg>
        <input
          v-model="keyword"
          class="salon-search-input"
          type="text"
          name="salon_location_search"
          :placeholder="$t('salon.location.search')"
          :aria-label="$t('salon.location.search')"
        />
      </div>

      <div class="salon-outlets" role="radiogroup" :aria-label="$t('salon.location.outletsGroup')">
        <component
          :is="outlet.is_open ? 'label' : 'div'"
          v-for="outlet in filteredOutlets"
          :key="outlet.id"
          class="salon-card"
          :class="{
            'salon-card--selected': outlet.is_open && outlet.branch_code === selectedCode,
            'salon-card--closed': !outlet.is_open,
          }"
          :role="outlet.is_open ? 'radio' : null"
          :aria-checked="outlet.is_open ? String(outlet.branch_code === selectedCode) : null"
          :aria-disabled="outlet.is_open ? null : 'true'"
        >
          <input
            v-if="outlet.is_open"
            v-model="selectedCode"
            class="salon-card-radio"
            type="radio"
            name="salon_outlet"
            :value="outlet.branch_code"
          />

          <div class="salon-card-media">
            <img v-if="outlet.icon_url" :src="outlet.icon_url" :alt="outlet.branch_name" />
            <svg v-else viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
              <path
                d="M4 9l1.5-4h13L20 9M4 9h16v9a1 1 0 01-1 1H5a1 1 0 01-1-1V9zm4 0v3m8-3v3"
                fill="none" stroke="currentColor" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="salon-card-body">
            <div class="salon-card-head">
              <div class="salon-card-titles">
                <h3 class="salon-card-name">{{ outlet.branch_name }}</h3>
                <p v-if="outlet.address_text" class="salon-card-address">{{ outlet.address_text }}</p>
              </div>
              <button
                v-if="outlet.destination_link"
                class="salon-map-btn"
                type="button"
                @click.prevent.stop="openMap(outlet)"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zm0 0v14m6-12v14"
                    fill="none" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>{{ $t("salon.location.viewMap") }}</span>
              </button>
            </div>

            <div class="salon-card-status">
              <span class="salon-status-dot" :class="{ 'salon-status-dot--closed': !outlet.is_open }"></span>
              <span class="salon-status-text" :class="{ 'salon-status-text--closed': !outlet.is_open }">
                {{ outlet.open_status_text }}
              </span>
            </div>

            <span v-if="outlet.is_open && outlet.branch_code === selectedCode" class="salon-card-check">
              <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                <circle cx="12" cy="12" r="10" fill="currentColor" />
                <path d="M8 12.5l2.5 2.5L16 9.5" fill="none" stroke="#ffffff" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>
        </component>

        <p v-if="!isLoading && !filteredOutlets.length" class="salon-empty">
          {{ keyword ? $t("salon.location.noMatch") : $t("salon.location.noOutlets") }}
        </p>
      </div>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" :disabled="!selectedCode" @click="goToNextStep">
        <span>{{ $t("salon.flow.continue") }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import OrderService from "@/services/order.service";
import SalonBookingService from "@/services/salonBooking.service";
import TokenService from "@/services/token.service";
import { localStorageName } from "@/config/utils";
import "@/salon/styles/tokens.css";

// SH001 is the first of the nine salon booking steps (Salon_UI/select_location).
const STEP = 1;
const TOTAL_STEPS = 9;

export default {
  name: "SalonSelectLocation",
  components: {
    MaterialLoading,
  },
  emits: ["selected", "bookings"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      // SH020 — whether this member has any appointments to go back to.
      hasBookings: false,
      outlets: [],
      keyword: "",
      selectedCode: this.$route.params.branch_code || "",
    };
  },
  computed: {
    progressWidth() {
      return ((STEP / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },
    // Filtered in the browser rather than through the endpoint's search_keyword, which
    // only matches branch_name — a shopper searching "Yishun" means the address.
    filteredOutlets() {
      const keyword = this.keyword.trim().toLowerCase();

      if (!keyword) {
        return this.outlets;
      }

      return this.outlets.filter((outlet) => {
        const haystack = [outlet.branch_name, outlet.address_text].join(" ").toLowerCase();
        return haystack.includes(keyword);
      });
    },
  },
  created() {
    this.getOutlets();
    this.checkBookingHistory();
  },
  methods: {
    async getOutlets() {
      // App.vue stores branch_info from /check-merchant-and-branch before this view
      // mounts, so the physical branch this outlet belongs to is already known — the
      // picker lists the sale channels sitting under that same physical branch.
      const branchInfo = JSON.parse(localStorage.getItem(localStorageName("BRANCH_INFO")) || "{}");
      const physicalBranchId = branchInfo.physical_branch_id || 0;

      if (!physicalBranchId) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await OrderService.getBranches({ physical_branch_id: physicalBranchId });

        if (response.data.result.isSuccess) {
          this.outlets = response.data.data.branches ?? [];
          this.applyDefaultSelection();
        }
      } catch (error) {
        this.outlets = [];
      } finally {
        this.isLoading = false;
      }
    },

    // The outlet in the URL starts out selected; if it is closed (or gone) fall back to
    // the first outlet that can actually take a booking, so Continue is never a dead end.
    applyDefaultSelection() {
      const current = this.outlets.find((outlet) => outlet.branch_code === this.selectedCode);

      if (current && current.is_open) {
        return;
      }

      const firstOpen = this.outlets.find((outlet) => outlet.is_open);
      this.selectedCode = firstOpen ? firstOpen.branch_code : "";
    },

    openMap(outlet) {
      window.open(outlet.destination_link, "_blank", "noopener");
    },

    /**
     * SH020 — does this customer have a booking history behind the top-bar arrow?
     *
     * Asked quietly, outside the loading overlay: the outlet list is what this screen is
     * for, and a signed-out customer or a call that fails simply leaves the arrow off
     * rather than blocking the picker behind a spinner.
     */
    async checkBookingHistory() {
      if (!this.isMemberSignedIn()) {
        return;
      }

      try {
        const response = await SalonBookingService.myBookings();

        if (response.data.result.isSuccess) {
          const data = response.data.data ?? {};
          this.hasBookings = !!(
            (data.upcoming && data.upcoming.length) || (data.past && data.past.length)
          );
        }
      } catch (error) {
        this.hasBookings = false;
      }
    },

    isMemberSignedIn() {
      try {
        return !!TokenService.getLocalAccessToken();
      } catch (error) {
        // getLocalAccessToken() reaches through user.data.authorisation without guarding
        // each hop, so a half-written member blob throws rather than returning null.
        return false;
      }
    },

    goToNextStep() {
      if (!this.selectedCode) {
        return;
      }

      // SH002: the flow container (BookingFlow.vue) owns what comes next — the phone +
      // PIN step. This screen no longer leaves the flow on its own.
      // SH004: the outlet name rides along so the service picker can print it without
      // asking the branch endpoint again.
      // SH009/SH010: so do the address and the map link. The review screen prints the
      // address under the outlet name and the ticket's "Get directions" opens the link —
      // and by then the flow has moved off the branch endpoint entirely.
      const outlet = this.outlets.find((item) => item.branch_code === this.selectedCode);

      this.$emit("selected", {
        branch_code: this.selectedCode,
        branch_name: outlet ? outlet.branch_name : "",
        branch_id: outlet ? outlet.id : 0,
        address_text: outlet ? outlet.address_text || "" : "",
        postal_code: outlet ? outlet.postal_code || "" : "",
        destination_link: outlet ? outlet.destination_link || "" : "",
        // SH026: the outlet decides the order the rest of the booking asks its questions
        // in — date first, stylist first, or the customer's choice.
        salon_booking_flow: outlet ? Number(outlet.salon_booking_flow) || 1 : 1,
        // SH032: and whether it quotes a price while the customer is still choosing.
        // Off unless the outlet says otherwise — an outlet saved before this setting
        // existed comes back without the key, and it has never quoted online.
        salon_show_service_price: outlet
          ? Number(outlet.salon_show_service_price) || 0
          : 0,
      });
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-location {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-primary);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-progress {
  margin-top: 16px;
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary-container);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
  padding: var(--salon-stack-lg) var(--salon-container-padding) 120px;
}

.salon-intro {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
  margin-bottom: 8px;
}

.salon-intro-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-background);
}

.salon-intro-subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Search ────────────────────────────────────────────────────── */
.salon-search {
  position: relative;
  width: 100%;
}

.salon-search-icon {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  color: var(--salon-on-surface-variant);
  pointer-events: none;
}

.salon-search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  line-height: 24px;
  color: var(--salon-on-background);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-input);
  box-shadow: 0 1px 2px rgba(28, 28, 30, 0.04);
  outline: none;
  appearance: none;
  transition: border-color 0.2s ease;
}

.salon-search-input::placeholder {
  color: var(--salon-on-surface-variant);
}

.salon-search-input:focus {
  border-color: var(--salon-primary-container);
}

/* ── Outlet cards ──────────────────────────────────────────────── */
.salon-outlets {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
  margin-top: 8px;
}

.salon-card {
  position: relative;
  display: flex;
  overflow: hidden;
  background: var(--salon-surface-lowest);
  border: 1px solid transparent;
  border-radius: var(--salon-radius-card);
  box-shadow: var(--salon-shadow-soft);
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.salon-card:active {
  transform: scale(0.98);
}

.salon-card--selected {
  border: 1.5px solid var(--salon-secondary);
}

.salon-card--closed {
  cursor: default;
  opacity: 0.5;
  filter: grayscale(0.3);
}

.salon-card--closed:active {
  transform: none;
}

.salon-card-radio {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.salon-card-media {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 96px;
  /* Stretches to the card's height so the photo column never ends short of the card,
     the way it does in the reference when the address line wraps. */
  height: auto;
  min-height: 96px;
  overflow: hidden;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
}

.salon-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.salon-card-body {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  padding: 16px;
}

.salon-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.salon-card-titles {
  min-width: 0;
}

.salon-card-name {
  margin: 0;
  overflow: hidden;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-background);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-card-address {
  margin: 2px 0 0;
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-map-btn {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 1px solid var(--salon-surface-variant);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.salon-map-btn:hover {
  background: var(--salon-surface-container);
}

.salon-card-status {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
}

.salon-status-dot {
  width: 6px;
  height: 6px;
  background: var(--salon-open);
  border-radius: 999px;
}

.salon-status-dot--closed {
  background: var(--salon-error);
}

.salon-status-text {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-open-text);
}

.salon-status-text--closed {
  color: var(--salon-error);
}

.salon-card-check {
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: flex;
  color: var(--salon-secondary);
}

.salon-empty {
  padding: 24px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
